import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, googleProvider } from '../services/firebase';
import { bootstrapUser } from '../services/users';
import { logGlobalError } from '../utils/globalErrorLogger';
import { UserType } from '../types';

export type GoogleSignInMode = 'popup' | 'redirect';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  loading: boolean;
  userType: UserType;
  isAdmin: boolean;
  canAccessApp: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (mode?: GoogleSignInMode) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectLoading, setRedirectLoading] = useState(true);
  const [userType, setUserType] = useState<UserType>('unknown');
  const [isAdmin, setIsAdmin] = useState(false);
  const [canAccessApp, setCanAccessApp] = useState(false);
  const initSeqRef = useRef(0);

  async function signUp(email: string, password: string, displayName: string) {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(user, { displayName });
  }

  async function signIn(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function signInWithGoogle(mode: GoogleSignInMode = 'popup') {
    if (mode === 'redirect') {
      await signInWithRedirect(auth, googleProvider);
      return;
    }
    await signInWithPopup(auth, googleProvider);
  }

  async function logout() {
    await signOut(auth);
  }

  async function resetPassword(email: string) {
    await sendPasswordResetEmail(auth, email);
  }

  useEffect(() => {
    let mounted = true;
    let roleUnsub: (() => void) | null = null;

    void getRedirectResult(auth)
      .then((result) => {
        if (mounted && result?.user) {
          setLoading(true);
          setCurrentUser(result.user);
        }
      })
      .catch((err) => {
        logGlobalError(err, { context: 'AuthContext.getRedirectResult' });
      })
      .finally(() => {
        if (mounted) setRedirectLoading(false);
      });

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const seq = initSeqRef.current + 1;
      initSeqRef.current = seq;

      if (roleUnsub) {
        roleUnsub();
        roleUnsub = null;
      }

      const init = async () => {
        setLoading(true);
        setCurrentUser(user);

        if (!user) {
          if (mounted && initSeqRef.current === seq) {
            setIsAdmin(false);
            setUserType('unknown');
            setCanAccessApp(false);
            setLoading(false);
          }
          return;
        }

        try {
          const roles = await bootstrapUser(user);
          if (mounted && initSeqRef.current === seq) {
            setUserType(roles.userType);
            setIsAdmin(roles.isAdmin);
            setCanAccessApp(roles.canAccessApp);
          }
        } catch (err) {
          logGlobalError(err, {
            context: 'AuthContext.bootstrapUser',
            metadata: { uid: user.uid },
          });
          if (mounted && initSeqRef.current === seq) {
            setIsAdmin(false);
            setUserType('unknown');
            setCanAccessApp(false);
          }
        } finally {
          if (mounted && initSeqRef.current === seq) {
            setLoading(false);
          }
        }

        const userRef = doc(db, 'users', user.uid);
        roleUnsub = onSnapshot(
          userRef,
          (snap) => {
            if (!mounted || initSeqRef.current !== seq) return;
            const data = snap.data();
            if (!data) return;
            const nextType =
              data.userType === 'admin' || data.userType === 'sales' || data.userType === 'unknown'
                ? data.userType
                : data.isAdmin === true
                  ? 'admin'
                  : 'unknown';
            setUserType(nextType);
            setIsAdmin(nextType === 'admin');
            setCanAccessApp(nextType === 'admin' || nextType === 'sales');
          },
          (err) => logGlobalError(err, { context: 'AuthContext.roleSubscription' }),
        );
      };

      void init();
    });

    return () => {
      mounted = false;
      initSeqRef.current += 1;
      if (roleUnsub) roleUnsub();
      unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading: loading || redirectLoading,
    userType,
    isAdmin,
    canAccessApp,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
